import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SortableHeader, SortIcon } from './SortableHeader.js';
import { DropdownFilter } from './DropdownFilter.js';
import type { SortDir } from './SortableHeader.js';
import { useKpiBugs } from '../../../hooks/index.js';
import { extractDateString } from '../../../lib/date-utils.js';
import type { BugDetail } from '../../../types/api.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business インシデントレベル文字列を受け取り、レベル3は赤（#ef4444）、レベル2はオレンジ（#f97316）のカラーコードを返す
 * @graph-connects none
 */
export function incidentColor(level: string | null): string | undefined {
  if (!level) return undefined;
  if (level.includes('3')) return '#ef4444';
  if (level.includes('2')) return '#f97316';
  return undefined;
}

/**
 * 列幅の定義（`<colgroup>` 用）。`table-fixed` と組み合わせて使う。
 *
 * - **`table-fixed` が必須**: 既定の auto layout は本文セルの最長テキストで列幅を決めるため、
 *   原因・起因案件名のような長文列が際限なく伸びる（実測でテーブル幅 2989px / 表示領域 1650px。
 *   うち「原因」列だけで 1016px を占めていた）。fixed にすると本文は列内で折り返し、
 *   `line-clamp-2` が意図どおり効く。
 * - **各幅はヘッダー文字列の実測 intrinsic 幅以上**にしてある。ヘッダーは `whitespace-nowrap`
 *   なので、幅が足りないと折り返す代わりに見出しが切れる（実測で 14 列中 9 列が不足していた）。
 *   幅は **ja / en 両方の実測 intrinsic 幅の最大値以上**にすること（日本語の方が広い列もある:
 *   「起因案件リリース日」は ja 175px / en 154px）。ラベルを変える・locale を追加するときは
 *   幅も併せて見直すこと。
 * @graph-connects none
 */
const BUG_COLUMN_WIDTHS = [
  136, // 起票日 / Created Date（実測 en 132px。ja は短いので en が支配的）
  152, // 発見日 / Discovery Date（実測 en 147px）
  178, // 起因案件リリース日 / Cause Released（実測 ja 175px / en 154px）
  96, // レベル / Level（フィルタ caret + ソートアイコン込み）
  120, // QA責任 / QA Resp.
  152, // 課題キー（AIRCLOSET-150158 が収まる幅）
  220, // 課題タイトル（本文は line-clamp-2 で 2 行に抑える）
  88, // i-point
  130, // 発見者 / Found By
  160, // 起因案件キー
  190, // 起因案件名 / Cause Name
  194, // 起因案件開発リーダー / Cause Dev Leader（実測 ja 191px / en 172px）
  170, // 原因 / Cause
  175, // 開発リーダー所属 / Dev Leader Group（実測 en 170px）
] as const;

/** colgroup の合計幅。表の min-width に使い、狭い画面では横スクロールさせる @graph-connects none */
const BUG_TABLE_MIN_WIDTH = BUG_COLUMN_WIDTHS.reduce((sum, w) => sum + w, 0);

/**
 * 列ヘッダー共通クラス。見出しは `whitespace-nowrap` で 1 行に固定する
 * （「起因案件リリース日」「起因案件開発リーダー」等が 2〜3 行に折れるとヘッダー行だけが
 * 不揃いに伸びて読みにくい）。`align-bottom` で高さの違う見出しの下端を揃える。
 * 幅は `BUG_COLUMN_WIDTHS`（colgroup）側で一元管理するのでここでは指定しない。
 * @graph-connects none
 */
const HEAD_CLASS =
  'text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap align-bottom';

type BugSortKey =
  | keyof Pick<
      BugDetail,
      | 'createdDate'
      | 'causeProjectReleasedDate'
      | 'incidentLevel'
      | 'qaResponsibility'
      | 'issueKey'
      | 'summary'
      | 'iPoint'
      | 'causeProjectKey'
      | 'causeProjectSummary'
      | 'causeProjectDevLeader'
      | 'cause'
      | 'foundBy'
    >
  | 'discoveryDate'
  | 'devLeaderGroup';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ詳細の日付列を YYYY-MM-DD 文字列に正規化する。/kpi/bugs は BigQuery の DATE を `{ value: 'YYYY-MM-DD' }` オブジェクトのまま返す（レスポンス schema で coerce していない）ため、素の値を描画すると React が "Objects are not valid as a React child" で落ちる。欠損は null にして呼び出し側で '-' に倒す
 * @graph-connects extractDateString [calls] BQ DATE / Date / 文字列を YYYY-MM-DD に正規化
 */
export function dateText(value: unknown): string | null {
  return extractDateString(value) || null;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグの発見日を返す。初回発生日（カスタムフィールド 170941）を優先し、空なら起票日にフォールバックする（バグ検知日数の「発見日」と同一定義）。BQ DATE オブジェクトも文字列へ正規化する
 * @graph-connects dateText [calls] 日付正規化
 */
export function discoveryDateOf(bug: Pick<BugDetail, 'createdDate' | 'firstOccurrenceDate'>) {
  return dateText(bug.firstOccurrenceDate) ?? dateText(bug.createdDate) ?? '-';
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ソートキーに対応するバグの比較値を返す。発見日は初回発生日優先・起票日フォールバック、開発リーダー所属はグループ名とチーム名の連結で比較する
 * @graph-connects discoveryDateOf [calls] 発見日（初回発生日優先）算出
 * @graph-connects dateText [calls] BQ DATE オブジェクトを含む日付列の正規化
 */
function sortValue(bug: BugDetail, key: BugSortKey): string | number | null {
  if (key === 'discoveryDate') return discoveryDateOf(bug);
  if (key === 'createdDate' || key === 'causeProjectReleasedDate') return dateText(bug[key]);
  if (key === 'devLeaderGroup') {
    return `${bug.devLeaderGroupName ?? ''} ${bug.devLeaderTeamName ?? ''}`.trim() || null;
  }
  return bug[key] ?? null;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ詳細テーブル。起票日・発見日（初回発生日優先）・起因案件リリース日・インシデントレベル・QA責任・issueKey・課題名・iPoint・発見者・起因案件Key・起因案件名・起因案件担当者（開発リーダー）・起因・開発リーダー所属を14列のレスポンシブテーブルで表示。インシデントレベル・QA責任フィルター・全列ソート対応
 * @graph-connects discoveryDateOf [calls] 発見日（初回発生日優先）算出
 * @graph-connects dateText [calls] BQ DATE オブジェクトを含む日付列の正規化
 */
export function BugTable({
  data,
}: {
  data: NonNullable<ReturnType<typeof useKpiBugs>['data']>['data'];
}) {
  const { t } = useTranslation();
  const [qaFilter, setQaFilter] = useState<string>('all');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<BugSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const levelOptions = useMemo(() => {
    const levels = new Set<string>();
    data.forEach((b) => {
      if (b.incidentLevel) levels.add(b.incidentLevel);
    });
    const sorted = [...levels].sort();
    return [
      { key: 'all', display: t('kpi.detail.qaFilterAll') },
      ...sorted.map((l) => ({ key: l, display: l })),
      { key: 'none', display: t('kpi.detail.qaFilterUnset') },
    ];
  }, [data, t]);

  const filteredData = useMemo(() => {
    let result = data;
    if (qaFilter !== 'all') {
      result =
        qaFilter === 'none'
          ? result.filter((b) => !b.qaResponsibility)
          : result.filter((b) => b.qaResponsibility === qaFilter);
    }
    if (levelFilter !== 'all') {
      result =
        levelFilter === 'none'
          ? result.filter((b) => !b.incidentLevel)
          : result.filter((b) => b.incidentLevel === levelFilter);
    }
    if (sortKey !== null) {
      const key = sortKey;
      result = [...result].sort((a, b) => {
        const av = sortValue(a, key);
        const bv = sortValue(b, key);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv), 'ja');
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [data, qaFilter, levelFilter, sortKey, sortDir]);

  const handleSort = (key: BugSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--color-text-muted)]">
        {t('kpi.detail.noItems')}
      </div>
    );
  }

  const qaOptions = [
    { key: 'all', display: t('kpi.detail.qaFilterAll') },
    { key: '有', display: '有' },
    { key: '無', display: '無' },
    { key: 'none', display: t('kpi.detail.qaFilterUnset') },
  ];

  const hasActiveFilter = levelFilter !== 'all' || qaFilter !== 'all';

  return (
    <div>
      {hasActiveFilter && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('kpi.detail.filterActive')}:
          </span>
          {levelFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--color-accent-primary)] text-white">
              {t('kpi.detail.level')}:{' '}
              {levelFilter === 'none' ? t('kpi.detail.qaFilterUnset') : levelFilter}
              <button
                onClick={() => {
                  setLevelFilter('all');
                }}
                className="ml-0.5 hover:opacity-70"
              >
                ×
              </button>
            </span>
          )}
          {qaFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--color-accent-primary)] text-white">
              {t('kpi.detail.qaResponsibility')}:{' '}
              {qaFilter === 'none' ? t('kpi.detail.qaFilterUnset') : qaFilter}
              <button
                onClick={() => {
                  setQaFilter('all');
                }}
                className="ml-0.5 hover:opacity-70"
              >
                ×
              </button>
            </span>
          )}
          <span className="text-xs text-[var(--color-text-muted)]">
            ({String(filteredData.length)}/{String(data.length)})
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table
          className="w-full text-sm table-fixed"
          style={{ minWidth: `${String(BUG_TABLE_MIN_WIDTH)}px` }}
        >
          <colgroup>
            {BUG_COLUMN_WIDTHS.map((width, index) => (
              <col key={index} style={{ width: `${String(width)}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.createdDate')}
                  sortKey="createdDate"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.discoveryDate')}
                  sortKey="discoveryDate"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.causeProjectReleasedDate')}
                  sortKey="causeProjectReleasedDate"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <div className="flex items-center gap-1">
                  <DropdownFilter
                    label={t('kpi.detail.level')}
                    options={levelOptions}
                    value={levelFilter}
                    onChange={setLevelFilter}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleSort('incidentLevel');
                    }}
                    className="hover:text-[var(--color-text-primary)] transition-colors"
                  >
                    <SortIcon active={sortKey === 'incidentLevel'} dir={sortDir} />
                  </button>
                </div>
              </th>
              <th className={HEAD_CLASS}>
                <div className="flex items-center gap-1">
                  <DropdownFilter
                    label={t('kpi.detail.qaResponsibility')}
                    options={qaOptions}
                    value={qaFilter}
                    onChange={setQaFilter}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleSort('qaResponsibility');
                    }}
                    className="hover:text-[var(--color-text-primary)] transition-colors"
                  >
                    <SortIcon active={sortKey === 'qaResponsibility'} dir={sortDir} />
                  </button>
                </div>
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.issueKey')}
                  sortKey="issueKey"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.issueTitle')}
                  sortKey="summary"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <div className="flex justify-end">
                  <SortableHeader
                    label={t('kpi.detail.iPoint')}
                    sortKey="iPoint"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </div>
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.foundBy')}
                  sortKey="foundBy"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.causeProductKey')}
                  sortKey="causeProjectKey"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.causeProductSummary')}
                  sortKey="causeProjectSummary"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.causeProductDevLeader')}
                  sortKey="causeProjectDevLeader"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.cause')}
                  sortKey="cause"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th className={HEAD_CLASS}>
                <SortableHeader
                  label={t('kpi.detail.devLeaderGroup')}
                  sortKey="devLeaderGroup"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((bug, index) => (
              <tr
                key={`${bug.issueKey}-${String(index)}`}
                className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
              >
                <td className="py-2 px-2 text-[var(--color-text-primary)] whitespace-nowrap">
                  {dateText(bug.createdDate) ?? '-'}
                </td>
                <td className="py-2 px-2 text-[var(--color-text-primary)] whitespace-nowrap">
                  {discoveryDateOf(bug)}
                </td>
                <td className="py-2 px-2 text-[var(--color-text-primary)] whitespace-nowrap">
                  {dateText(bug.causeProjectReleasedDate) ?? '-'}
                </td>
                <td className="py-2 px-2 whitespace-nowrap">
                  {bug.incidentLevel ? (
                    <span
                      className="text-xs font-medium"
                      style={{ color: incidentColor(bug.incidentLevel) }}
                    >
                      {bug.incidentLevel}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">-</span>
                  )}
                </td>
                <td className="py-2 px-2 whitespace-nowrap">
                  {bug.qaResponsibility ? (
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        bug.qaResponsibility === '有'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {bug.qaResponsibility}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">-</span>
                  )}
                </td>
                <td className="py-2 px-3 whitespace-nowrap">
                  <a
                    href={`https://air-closet.backlog.jp/view/${bug.issueKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-accent-primary)] hover:underline"
                  >
                    {bug.issueKey}
                  </a>
                </td>
                <td className="py-2 px-3 text-[var(--color-text-primary)]">
                  <div className="line-clamp-2">{bug.summary}</div>
                </td>
                <td className="py-2 px-2 text-right text-[var(--color-text-secondary)] whitespace-nowrap">
                  {bug.iPoint ?? '-'}
                </td>
                <td className="py-2 px-3 text-[var(--color-text-secondary)] whitespace-nowrap">
                  {bug.foundBy ?? '-'}
                </td>
                <td className="py-2 px-3 whitespace-nowrap">
                  {bug.causeProjectKey ? (
                    <a
                      href={`https://air-closet.backlog.jp/view/${bug.causeProjectKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-accent-primary)] hover:underline"
                    >
                      {bug.causeProjectKey}
                    </a>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">-</span>
                  )}
                </td>
                <td className="py-2 px-3 text-[var(--color-text-primary)]">
                  <div className="line-clamp-2">{bug.causeProjectSummary ?? '-'}</div>
                </td>
                <td className="py-2 px-3 text-[var(--color-text-secondary)] whitespace-nowrap">
                  {bug.causeProjectDevLeader ?? '-'}
                </td>
                <td className="py-2 px-3 text-[var(--color-text-secondary)]">
                  <div className="line-clamp-2">{bug.cause ?? '-'}</div>
                </td>
                <td className="py-2 px-3 text-[var(--color-text-secondary)] whitespace-nowrap">
                  {bug.devLeaderGroupName || bug.devLeaderTeamName
                    ? `${bug.devLeaderGroupName ?? ''} ${bug.devLeaderTeamName ?? ''}`.trim()
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
